import { CommonModule, Location } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, computed, effect, inject, NgZone, OnInit, signal, Signal, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import { debounceTime, merge, take } from 'rxjs';
import { ProjectRole, ValidityStatus } from '../../../models/enums';
import { Project } from '../../../models/project';
import { ProjectService } from '../../../services/project.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { TableStateService } from '../../../services/table-state.service';
import { getGridTemplate, mapObject, MY_DATE_FORMATS } from '../../../utils/utils';
import { AdvancedSelectComponent, AdvancedSelectValue, createAdvancedSelectValue } from "../../misc/advanced-select/advanced-select.component";
import { ColumnSelectorDialogComponent } from '../../misc/column-selector-dialog/column-selector-dialog.component';
import { enumToSortedNestedOptions, mapToNestedOptions } from '../../misc/nested-options';

interface ProjectFilters {
  textFilter: string;
  activeRange: {
    start: DateTime | null;
    end: DateTime | null;
  }
  topics: AdvancedSelectValue;
  euficRoles: AdvancedSelectValue;
}


@Component({
  selector: 'app-projects',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    AdvancedSelectComponent,
    ReactiveFormsModule,
    AdvancedSelectComponent
  ],
  providers: [
    provideLuxonDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
  ],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss'
})
export class ProjectsComponent implements OnInit, AfterViewInit {
  readonly ValidityStatus = ValidityStatus;
  tableId = 'projects'
  searchText = '';
  loading: boolean = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('filtersPanel') filtersPanel!: MatExpansionPanel;

  dataSource = new MatTableDataSource<Project>();
  allColumns = [
    { key: 'valid', label: 'Valid', type: 'icon' },
    { key: 'shortName', label: 'Short name', type: 'text' },
    // { key: 'fullName', label: 'Full name', type: 'text' },
    { key: 'startDate', label: 'Start date', type: 'text' },
    { key: 'endDate', label: 'End date', type: 'text' },
    { key: 'website', label: 'Website', type: 'text' },
    { key: 'euficRole', label: 'EUFIC Role', type: 'chips' },
    { key: 'topics', label: 'Topics of interest', type: 'chips' },
    { key: 'lastModifiedOn', label: 'Last modified on', type: 'text' },
  ];
  columnLabelMap: Record<string, string> = {};

  defaultSelectedColumns: string[] = ['valid', 'shortName', 'startDate', 'endDate', 'euficRole', 'topics'];
  defaultColumnsOrder: string[] = this.allColumns.map(v => v.key);

  selectedColumns: string[] = this.defaultSelectedColumns;
  pageEvent: PageEvent | undefined;

  readonly showAllOrAny: Signal<boolean> = signal(false);

  readonly refDataService = inject(ReferenceDataService);
  readonly allTopics = this.refDataService.getGeneralTopicsOfInterest();
  readonly allTopicOptions = computed(() => mapToNestedOptions(this.allTopics(), true));
  readonly allProjectRoles = Object.values(ProjectRole);
  readonly allProjectRoleOptions = enumToSortedNestedOptions(ProjectRole);

  filterForm: FormGroup;
  filters: ProjectFilters = this.getDefaultFilters();
  filtersExpanded: boolean = true;

  resetCounter = 0; // used to reset the filter form

  constructor(private readonly projectService: ProjectService, private readonly tableStateService: TableStateService,
    private readonly dialog: MatDialog, private readonly snackBar: MatSnackBar, private readonly router: Router,
    private readonly location: Location, private readonly cdr: ChangeDetectorRef, private readonly zone: NgZone,
    private readonly formBuilder: FormBuilder) {
    this.filterForm = {} as FormGroup;
  }

  ngOnInit(): void {
    this.columnLabelMap = Object.fromEntries(
      this.allColumns.map(col => [col.key, col.label])
    );
    this.loadProjects();

    // Display and clear any navigation message
    const state = this.location.getState() as { message?: string };
    if (state?.message) {
      this.snackBar.open(state.message, 'Close', {
        duration: 3000,
        panelClass: ['snackbar-success']
      });
      // Clear the message from history so it doesn't persist on reload
      history.replaceState({}, document.title, location.pathname);
    }

    this.filterForm = this.formBuilder.group({
      textFilter: '',
      topics: { selectedValues: [], matchAll: false },
      areasOfWork: { selectedValues: [], matchAll: false },
      audiences: { selectedValues: [], matchAll: false },
      fundingSchemes: { selectedValues: [], matchAll: false },
      euficRoles: { selectedValues: [], matchAll: false },
      // DateTime stored as ISO strings for better portability
      activeRange: this.formBuilder.group({
        start: new FormControl<string | null>(null),
        end: new FormControl<string | null>(null),
      })
    })
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = this.filterFunction;

    const saved = this.tableStateService.getState<ProjectFilters>(this.tableId);
    if (saved) {
      // console.debug('restoring', saved)
      this.zone.onStable.pipe(take(1)).subscribe(() => {
        this.paginator.pageIndex = saved.pageIndex;
        this.paginator.pageSize = saved.pageSize;
        this.sort.active = saved.sortActive;
        this.sort.direction = saved.sortDirection;
        this.filterForm.patchValue(saved.filters ? saved.filters : this.getDefaultFilters());
        this.filtersExpanded = saved.filtersExpanded;
        this.selectedColumns = saved.selectedColumnKeys ?? this.defaultSelectedColumns;
        if (saved.allColumnKeys) {
          this.allColumns.sort(
            (a, b) => saved.allColumnKeys.indexOf(a.key) - saved.allColumnKeys.indexOf(b.key)
          );
        }
        this.cdr.detectChanges();
      });
    }

    merge(
      this.filtersPanel.expandedChange,
      this.paginator.page,
      this.sort.sortChange,
      this.filterForm.valueChanges.pipe(debounceTime(300))
    ).subscribe(() => {
      this.saveState();
    });
    this.filterForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe(filters => {
        if (filters) {
          this.dataSource.filter = JSON.stringify(filters);
        }
      });
  }

  get displayedColumns(): string[] {
    return this.selectedColumns;
  }

  get activeRange(): FormGroup {
    return this.filterForm?.get('activeRange') as FormGroup
  }

  loadProjects(): void {
    this.loading = true;
    this.projectService.getAllProjects().subscribe({
      next: projects => {
        this.dataSource.data = projects;
        this.loading = false;
      },
      error: msg => {
        console.error('Failed to load projects:', msg);
        this.snackBar.open('Failed to load projects', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error']
        }); this.loading = false;
      }
    });
  }

  saveState() {
    const state = {
      pageIndex: this.paginator?.pageIndex ?? 0,
      pageSize: this.paginator?.pageSize ?? 10,
      sortActive: this.sort?.active,
      sortDirection: this.sort?.direction,
      filters: this.filterForm.getRawValue(),
      filtersExpanded: this.filtersExpanded,
      allColumnKeys: this.allColumns.map(c => (c.key)),
      selectedColumnKeys: this.selectedColumns,
    }
    // console.debug('Saving state', state);
    this.tableStateService.setState(this.tableId, state);
  }

  goToDetail(project: Project): void {
    this.saveState();
    this.router.navigate(['/projects', project.id]).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  add(): void {
    this.saveState();
    this.router.navigate(['/projects', 'new']);
  }

  delete(id: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Event',
        message: 'Are you sure you want to delete this project?'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.projectService.delete(id).subscribe({
          next: () => {
            this.loadProjects();
            this.snackBar.open('Project deleted', 'Close', { duration: 3000 });
          },
          error: msg => {
            this.snackBar.open('Failed to delete project', 'Close', { duration: 3000 });
            console.error('Delete failed:', msg)
          }
        });
      }
    });
  }

  export(): void {
    const data: any[] = [];
    this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort!).forEach(d => {
      data.push(mapObject(d, this.selectedColumns));
    });

    const wb = XLSX.utils.book_new();
    wb.Props ??= {};
    wb.Props.Title = 'List of projects';

    const ws = XLSX.utils.json_to_sheet(data, { cellDates: true, dateNF: 'dd/mm/YYYY' });
    const range = XLSX.utils.decode_range(ws['!ref']!);
    let col_index = XLSX.utils.decode_col("W");
    for (let row = range.s.r + 1; row <= range.e.r; ++row) {
      const ref = XLSX.utils.encode_cell({ r: row, c: col_index })
      if (ws[ref]?.t === 'd') {
        ws[ref].z = 'mm/YYYY';
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, "Projects");
    XLSX.writeFileXLSX(wb, "projects.xlsx");
  }

  /**
   * creates the template for the table columns
   */
  get gridTemplate() {
    return getGridTemplate(this.selectedColumns, this.allColumns);
  }

  openColumnSelector(): void {
    const dialogRef = this.dialog.open(ColumnSelectorDialogComponent, {
      data: {
        allColumns: this.allColumns,
        selected: [...this.selectedColumns],
        defaultSelection: [...this.defaultSelectedColumns],
        defaultOrder: [...this.defaultColumnsOrder]
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.selectedColumns = result.selected;
        this.allColumns = result.allColumns;
        this.saveState();
      }
    });
  }

  getDefaultFilters(): ProjectFilters {
    return {
      textFilter: '',
      activeRange: {
        start: null,
        end: null
      },
      topics: createAdvancedSelectValue(),
      euficRoles: createAdvancedSelectValue(),
    };
  }

  clearAllFilters() {
    console.debug('clear all filters')
    this.resetCounter++; // increment → triggers ngOnChanges in children
    this.filterForm.reset();
  }

  toggleFilterMatch(fieldName: keyof ProjectFilters) {
    this.filterForm.get(fieldName)?.setValue(!this.filterForm.get(fieldName)?.value);
  }

  togglePanel(event: MouseEvent) {
    event.stopPropagation(); // prevent header click from toggling
    this.filtersExpanded = !this.filtersExpanded;
  }

  /**
   * 
   * @param data 
   * @param filter 
   * @returns 
   */

  // I have to defined it this way so that the this context is preserved

  filterFunction = (data: Project, filter: string) => {
    const rawFilters = JSON.parse(filter);
    const filters: ProjectFilters = {
      ...rawFilters,
      activeRange: {
        start: rawFilters.activeRange?.start ? DateTime.fromISO(rawFilters.activeRange.start) : null,
        end: rawFilters.activeRange?.end ? DateTime.fromISO(rawFilters.activeRange.end) : null
      },
    };

    if (filters?.textFilter) {
      const searchStr = filters.textFilter.toLowerCase();
      const inShortName = data.name?.toLowerCase().includes(searchStr);
      const inFullName = data.fullName?.toLowerCase().includes(searchStr);
      if (!inShortName && !inFullName) {
        return false;
      }
    }

    const startDate = data.startDate ? DateTime.fromISO(data.startDate) : null;
    const endDate = data.endDate ? DateTime.fromISO(data.endDate) : null;
    const filterStart = filters?.activeRange.start ?? null;
    const filterEnd = filters?.activeRange.end ?? null;
    if (filterStart && filterEnd) {
      // those that were active between those two dates
      // for items with both startDate and endDate, filter out items that started after the end of the period or ended before the start of the period
      // for items with startDate only, filter out items with startDate outside of the period
      // for items with endDate only, filter out items with startDate outside of the period
      // and ended before the end of the period
      if (startDate && endDate) {
        if ((startDate > filterEnd) || (endDate < filterStart)) {
          return false;
        }
      } else if (startDate && !endDate) {
        if (startDate > filterEnd) {
          return false;
        }
      } else if (!startDate && endDate) {
        if (endDate < filterStart) {
          return false;
        }
      }
    } else if (filterStart || filterEnd) {
      let activeDate: DateTime | null = (filterStart ? filterStart : filterEnd)
      console.debug('activeDate', activeDate, filterStart, filterEnd, startDate, endDate);
      // those that were active on or after this date
      // for items with both startDate and endDate, filter out items that started after or ended before the date
      // for items with startDate only, filter out items that started after the date
      // for items with endDate only, filter out items that ended before the date
      console.debug((startDate! > activeDate!), (endDate! < activeDate!))
      if (startDate && endDate) {
        if ((startDate > activeDate!) || (endDate < activeDate!)) {
          return false;
        }
      } else if (startDate && !endDate) {
        if (startDate > activeDate!) {
          return false;
        }
      } else if (!startDate && endDate) {
        if (endDate < activeDate!) {
          return false;
        }
      }
    }

    if (filters?.topics?.selectedValues && filters.topics?.selectedValues.length > 0) {
      const intersection = data.topics.filter(item => filters.topics?.selectedValues.includes(String(item.id)));
      if (filters.topics.matchAll) {
        if (intersection.length < filters.topics?.selectedValues.length) {
          return false
        }
      } else if (intersection.length === 0) {
        return false;
      }
    }
    if (filters?.euficRoles?.selectedValues && filters.euficRoles?.selectedValues.length > 0) {
      if (!data.euficRole) {
        return false;
      }
      const intersection = [data.euficRole].filter(item => filters.euficRoles?.selectedValues.includes(String(item)));
      if (filters.euficRoles.matchAll) {
        if (intersection.length < filters.euficRoles?.selectedValues.length) {
          return false
        }
      } else if (intersection.length === 0) {
        return false;
      }
    }
    return true;
  }
}